import { VisualisationContextProps } from 'widgets/supportVisualisation/Content/VisualisationContentProps';
import React, { useState } from 'react';
import { Menu } from '@instinet-analytics/semantic-ui-components';
import { toDateTimeString } from 'widgets/supportVisualisation/SupportVisualisationHelpers';
import { StreamedDataKey } from 'streamsState/store/streamedData/StreamedDataState';
import {
    MarketDataBarItem,
    MarketDataState,
} from 'marketData/store/MarketDataState';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { showNiceDate } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import { ColDef } from 'ag-grid-community/dist/lib/entities/colDef';
import './SupportVisualisationMarketData.scss';
import '../../SupportVisualisationMenu.scss';
import orderBy from 'lodash/orderBy';

type FlattenedData = { key: string; items: MarketDataBarItem[] };

export default function SupportVisualisationMarketData(
    props: VisualisationContextProps
) {
    const { state, width, height } = props;

    const style = { width, height };

    const [currentKey, setCurrentKey] = useState<StreamedDataKey | undefined>();
    const [currentTdKey, setCurrentTdKey] = useState<string | undefined>();

    const marketData = state?.state.marketData;
    if (!marketData) {
        return <div>None In State</div>;
    }

    const data = orderBy(flattenData(marketData), ['time']);

    const streamedDataKeys = data.map((d) => d.key);
    const currentKeyItems = data.find((d) => d.key === currentKey)?.items ?? [];

    if (currentKeyItems) {
        for (const d of currentKeyItems) {
            showNiceDate(d, 'time');
        }
    }

    const refData = flattenRefData(marketData);

    return (
        <div
            className="SupportVisualisationMarketData streamed-data"
            style={style}
        >
            <div>
                <Menu
                    pointing
                    vertical
                    className="SupportVisualisationMenu streamedDataKeys"
                >
                    {refData.map((td) => (
                        <RefDataLink key={td.bbSymbol} td={td} />
                    ))}
                    {streamedDataKeys.map((u) => (
                        <KeyLink
                            key={u}
                            streamedDataKey={u as StreamedDataKey}
                        />
                    ))}
                </Menu>
            </div>

            {currentKey && (
                <WidgetPanelGrid
                    data={currentKeyItems}
                    getRowDataId={(d) => d.time.toString()}
                    csvFileName={'market-data'}
                    colDefs={
                        currentKey.startsWith('Bars')
                            ? getBarColDefs()
                            : getTicksColDefs()
                    }
                />
            )}

            {currentTdKey && (
                <RefDataPanel
                    td={refData.find((t) => t.bbSymbol === currentTdKey)!}
                />
            )}
        </div>
    );

    function onChangeCurrentKey(nextKey: StreamedDataKey) {
        setCurrentTdKey(undefined);
        setCurrentKey(nextKey);
    }

    function onChangeCurrentTdKey(sym: string) {
        setCurrentTdKey(sym);
        setCurrentKey(undefined);
    }

    function KeyLink(props: { streamedDataKey: StreamedDataKey }) {
        const { streamedDataKey } = props;
        return (
            <Menu.Item
                active={streamedDataKey === currentKey}
                onClick={() => onChangeCurrentKey(streamedDataKey)}
            >
                {streamedDataKey}
            </Menu.Item>
        );
    }

    function RefDataLink(props: {
        td: {
            bbSymbol: string;
            startTradingHoursTime: Date;
            endTradingHoursTime: Date;
        };
    }) {
        const { td } = props;
        return (
            <Menu.Item
                active={td.bbSymbol === currentTdKey}
                onClick={() => onChangeCurrentTdKey(td.bbSymbol)}
            >
                {td.bbSymbol} Ref Data
            </Menu.Item>
        );
    }
}

function flattenData(marketData: MarketDataState): FlattenedData[] {
    return [
        ...marketData.bars.map((b) => ({
            key: `Bars/${b.symbol}/${b.binSize}`,
            items: b.bars,
        })),
        ...marketData.ticks.map((b) => ({
            key: `Ticks/${b.symbol}`,
            items: b.ticks,
        })),
    ];
}

function flattenRefData(marketData: MarketDataState): Array<{
    bbSymbol: string;
    startTradingHoursTime: Date;
    endTradingHoursTime: Date;
    averageVolume: number;
    pnc: number;
}> {
    return Object.keys(marketData.refData).map((bbSymbol) => {
        const refData = marketData.refData[bbSymbol];
        return {
            bbSymbol,
            startTradingHoursTime: new Date(refData.startTradingHoursTimestamp),
            endTradingHoursTime: new Date(refData.endTradingHoursTimestamp),
            averageVolume: refData.averageVolume,
            pnc: refData.pnc,
        };
    });
}

function getBarColDefs(): ColDef[] {
    const timeDisplay: ColDef = {
        field: 'timeDisplay',
        width: 130,
        resizable: true,
        headerName: 'Time',
    };
    const cols = ['open', 'close', 'high', 'low', 'volume', 'time'].map(
        (key) => ({
            field: key,
            key,
            width: 120,
            resizable: true,
        })
    );

    return [timeDisplay, ...cols];
}

function getTicksColDefs(): ColDef[] {
    const timeDisplay: ColDef = {
        field: 'timeDisplay',
        width: 130,
        resizable: true,
        headerName: 'Time',
    };
    const cols = ['lastPrice', 'high', 'low', 'volume', 'bid', 'ask'].map(
        (key) => ({
            field: key,
            key,
            width: 120,
            resizable: true,
        })
    );

    return [timeDisplay, ...cols];
}

function RefDataPanel(props: {
    td: {
        bbSymbol: string;
        startTradingHoursTime: Date;
        endTradingHoursTime: Date;
        averageVolume: number;
        pnc: number;
    };
}) {
    const { td } = props;
    return (
        <div className="TradingDayPanel">
            <div>
                Trading Hours Start:{' '}
                {toDateTimeString(td.startTradingHoursTime)}
            </div>
            <div>
                Trading Hours End: {toDateTimeString(td.endTradingHoursTime)}
            </div>
            <div>Average Volume: {td.averageVolume}</div>
            <div>PNC: {td.pnc}</div>
        </div>
    );
}
